import React, { ReactElement, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import useKeyboardShortcuts from "../../../contexts/KeyboardShortcutContext";
import useSelectionState, { Ticket } from "../../../pages/agent/selection-state";
import { NoDataOrError } from "../../../pages/error/Error";
import {
    ChatStatus, useAddParticipantsMutation, useParticiapntAddSettingsQuery,
} from "../../../__generated__/api";
import Confirm from "../../confirm/Confirm";
import Loading from "../../loading/Loading";
import Users from "../../pickers/users/Users";
import { SelectInstanceHandler } from "../../select/Select";

export default function ParticipantsAdd(): ReactElement {
    const selectionState = useSelectionState();
    const { data, loading, error } = useParticiapntAddSettingsQuery();
    const [addParticipants] = useAddParticipantsMutation();

    const [shouldShow, setShouldShow] = useState<boolean>(false);
    const [message, setMessage] = useState<string>("");
    const [selectedAgents, setSelectedAgents] = useState<string[]>();
    const selectRef = React.createRef<SelectInstanceHandler>();
    const [selectedTicketIds, setSelectedTicketIds] = useState<string[]>([]);

    const [canAddParticipants, setCanAddParticipants] = useState<boolean>(true);

    useEffect(() => {
        if (shouldShow) {
            selectRef.current?.focus();
        }
        setSelectedAgents(undefined);
        setMessage("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldShow]);

    const addParticipant = async () => {
        setShouldShow(false);
        await addParticipants({
            variables: {
                participants: selectedTicketIds.map(chatId => ({
                    chatId,
                    note: message,
                    participantIds: selectedAgents ?? [],
                    shouldAddSelf: !canAddParticipants,
                })),
            },
        });
    };

    const startAddParticipant = (tickets?: Ticket[]) => {
        const selected = tickets ?? selectionState.getSelection().filter(row => row.status === ChatStatus.Assigned
            || row.status === ChatStatus.OnHold || row.status === ChatStatus.Snoozed);
        setSelectedTicketIds(selected.toIdList("id"));
        if (selected.isNotEmpty) {
            setShouldShow(true);
        }
    };

    useKeyboardShortcuts(shortcuts => {
        shortcuts.on({
            shortcut: "k+p",
            context: "default",
            description: "Adds a participant to the conversation",
            group: "Conversations",
            handler: () => startAddParticipant(),
        });
    });

    selectionState.registerAddParticipantsHandler((tickets: Ticket[]) => {
        setCanAddParticipants(true);
        startAddParticipant(tickets);
    });

    selectionState.registerParticipateHandler((tickets: Ticket[]) => {
        setCanAddParticipants(false);
        startAddParticipant(tickets);
    });

    if (loading) {
        return <Loading />;
    }
    if (!data || error) {
        return <NoDataOrError error={error} />;
    }

    // const agentName = selectedAgents?.isNotEmpty ? ` ${selectedAgents.firstElement.firstName}` : "";
    return (
        <Confirm
            confirmText={canAddParticipants ? "Add Participants" : "Participate"}
            shouldShow={shouldShow}
            closeText="Close"
            onHide={() => setShouldShow(false)}
            onConfirm={() => addParticipant()}
        >
            <div className="flex-grow-1 p-3 d-flex flex-column">
                <h5 className="mb-4">
                    {canAddParticipants ? "Add Participants" : "Participate"}
                </h5>
                {canAddParticipants && (
                    <div className="mb-4">
                        <Form.Label className="text-primary">Participants</Form.Label>
                        <Users
                            isMulti
                            minRequired={1}
                            onSelected={selected => setSelectedAgents(selected)}
                            value={selectedAgents ?? []}
                        />

                        <Form.Text>
                            Who would you like to assign this ticket to?
                        </Form.Text>
                    </div>
                )}

                <Form.Label className="text-primary">
                    {canAddParticipants ? "Participant Comments" : "Comments"}
                </Form.Label>
                <Form.Control
                    as="textarea"
                    type="text"
                    placeholder={canAddParticipants
                        ? `Hey, adding you to this ticket to take a look.`
                        : "I wanted to add that..."}
                    className="default-text-height p-2"
                    onChange={evt => setMessage(evt.target.value)}
                />
                <Form.Text>
                    Any additional comments or context you want to leave for the participant.
                </Form.Text>

            </div>
        </Confirm>
    );
}

ParticipantsAdd.defaultProps = {
    refreshData: () => {
        // nothing
    },
};
